import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CallsBadge from '../components/CallsBadge';
import ImportantNotesButton from '../components/ImportantNotesButton';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { 
    UserPlus, Plus, Search, Eye, Trash2, User, AlertCircle, ArrowRight, Clock, Phone, ChevronUp, ChevronDown, BellRing
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUTS = [
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
    { value: 'sans_reponse_1', label: 'Sans réponse 1', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'sans_reponse_2', label: 'Sans réponse 2', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'sans_reponse_3', label: 'Sans réponse 3', color: 'bg-orange-100 text-orange-700' },
    { value: 'a_rappeler', label: 'À rappeler', color: 'bg-purple-100 text-purple-700' },
    { value: 'visite_virtuelle', label: 'Visite virtuelle', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'visite_presentiel', label: 'Visite présentiel', color: 'bg-green-100 text-green-700' },
    { value: 'devis_estimatif', label: 'Devis estimatif', color: 'bg-teal-100 text-teal-700' },
    { value: 'listing_attente', label: 'Listing en attente', color: 'bg-gray-100 text-gray-700' },
    { value: 'listing_telephone', label: 'Listing téléphone', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'listing_email', label: 'Listing email', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'faux_contact', label: 'Faux contact', color: 'bg-red-100 text-red-700' },
    { value: 'non_interessant', label: 'Non intéressant', color: 'bg-gray-100 text-gray-600' },
];

const STATUS_ROW_STYLES = {
    nouveau:            'bg-pink-100 text-pink-900 font-semibold',
    visite_presentiel:  'bg-emerald-100 text-emerald-900 font-semibold',
    visite_virtuelle:   'bg-emerald-100 text-emerald-900 font-semibold',
    visite_terminee:    'bg-emerald-200 text-emerald-900 font-semibold',
    faux_contact:       'bg-red-100 text-red-900 font-semibold',
    non_interessant:    'bg-red-100 text-red-900 font-semibold',
    a_rappeler:         'bg-orange-100 text-orange-900 font-semibold',
    sans_reponse_1:     'bg-amber-100 text-amber-900 font-semibold',
    sans_reponse_2:     'bg-amber-100 text-amber-900 font-semibold',
    sans_reponse_3:     'bg-amber-100 text-amber-900 font-semibold',
    listing_attente:    'bg-violet-100 text-violet-900 font-semibold',
    listing_telephone:  'bg-violet-100 text-violet-900 font-semibold',
    listing_email:      'bg-violet-100 text-violet-900 font-semibold',
    devis_estimatif:    'bg-violet-100 text-violet-900 font-semibold',
};

const getRowStyle = (statut) => STATUS_ROW_STYLES[statut] || '';

const SOURCES = [
    { value: 'appel_simple', label: 'Appel simple' },
    { value: 'appel_google', label: 'Appel Google' },
    { value: 'formulaire_site', label: 'Formulaire site' },
    { value: 'message_google', label: 'Message Google' },
    { value: 'recommandation', label: 'Recommandation' },
    { value: 'ancien_client', label: 'Ancien client' },
    { value: 'plateforme_externe', label: 'Plateforme externe' },
];

const getStatusLabel = (val) => STATUTS.find(s => s.value === val)?.label || val || '-';
const getStatusColor = (val) => STATUTS.find(s => s.value === val)?.color || 'bg-gray-100 text-gray-600';

export default function Prospects() {
    const navigate = useNavigate();
    const { canWrite, canDelete } = useCan('prospects');
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    // Source filter — applied client-side on top of the server-filtered list
    // (the API doesn't accept a source query param). The `source` field is
    // already projected in the response, so no extra fetch is needed.
    const [sourceFilter, setSourceFilter] = useState('all');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // History modal
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    // Bloc « Prospects à rappeler / listing en attente »
    const [rappelList, setRappelList] = useState([]);
    const [rappelExpanded, setRappelExpanded] = useState(true);

    useEffect(() => { fetchProspects(); }, [searchTerm, statusFilter]);
    useEffect(() => { fetchRappelList(); }, []);

    // Client-side narrowing by source. Empty/missing source is matched by the
    // dedicated "Non renseigné" option so admins can find rows that need a
    // source set.
    const displayedProspects = sourceFilter === 'all'
        ? prospects
        : prospects.filter(p => {
            const s = p.source || '';
            if (sourceFilter === '__none__') return !s;
            return s === sourceFilter;
        });

    const fetchRappelList = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/prospects-a-rappeler`, { withCredentials: true });
            setRappelList(Array.isArray(res.data) ? res.data : []);
        } catch { /* silent */ }
    };

    const fetchProspects = async () => {
        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter !== 'all') params.append('statut', statusFilter);
            const response = await axios.get(`${API_URL}/api/prospects?${params}`, { withCredentials: true });
            setProspects(response.data);
        } catch { toast.error('Erreur lors du chargement des prospects'); }
        finally { setLoading(false); }
    };

    const handleDelete = async () => {
        if (!selectedProspect) return;
        setSubmitting(true);
        try {
            await axios.delete(`${API_URL}/api/prospects/${selectedProspect.id}`, { withCredentials: true });
            toast.success('Prospect supprimé');
            setShowDeleteModal(false);
            setSelectedProspect(null);
            await fetchProspects();
        } catch { toast.error('Erreur lors de la suppression'); }
        finally { setSubmitting(false); }
    };

    const openHistory = async (prospect, e) => {
        e.stopPropagation();
        setLoadingHistory(true);
        setHistoryData(null);
        setShowHistoryModal(true);
        try {
            const res = await axios.get(`${API_URL}/api/prospects/${prospect.id}/history`, { withCredentials: true });
            setHistoryData(res.data);
        } catch { toast.error("Erreur lors du chargement de l'historique"); }
        finally { setLoadingHistory(false); }
    };

    const getStatusBadge = (statut) => (
        <Badge className={getStatusColor(statut)}>{getStatusLabel(statut)}</Badge>
    );

    const getSourceLabel = (source) => SOURCES.find(s => s.value === source)?.label || source || '-';

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try { return formatInTimeZone(new Date(dateStr), 'Europe/Paris', 'dd/MM/yyyy', { locale: fr }); }
        catch { return dateStr; }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        try { return formatInTimeZone(new Date(dateStr), 'Europe/Paris', "dd/MM/yyyy 'à' HH:mm", { locale: fr }); }
        catch { return dateStr; }
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            <div className="space-y-6 animate-fadeIn">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="prospects-title">
                            Prospects
                        </h1>
                        <p className="text-gray-500 mt-1">Gérez vos prospects et opportunités</p>
                    </div>
                    <Button onClick={() => navigate('/prospects/nouveau')}
                        disabled={!canWrite}
                        title={!canWrite ? 'Accès refusé : permission Écriture requise' : ''}
                        className="bg-[#C9A962] hover:bg-[#B8984F] text-white disabled:opacity-50 disabled:cursor-not-allowed" data-testid="create-prospect-button">
                        <Plus className="w-4 h-4 mr-2" />Nouveau Prospect
                    </Button>
                </div>

                {/* Bloc Prospects à rappeler / listing en attente */}
                {rappelList.length > 0 && (
                    <Card className="shadow-sm border-l-4 border-l-purple-500" data-testid="rappel-block">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center gap-2 text-[#1B365D]">
                                    <BellRing className="w-4 h-4 text-purple-500" />
                                    À rappeler / Listing en attente
                                    <Badge className="bg-purple-100 text-purple-700 border-0 ml-1">{rappelList.length}</Badge>
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => setRappelExpanded(v => !v)}
                                    data-testid="rappel-toggle"
                                >
                                    {rappelExpanded ? <><ChevronUp className="w-3 h-3 mr-1" />Masquer</> : <><ChevronDown className="w-3 h-3 mr-1" />Afficher</>}
                                </Button>
                            </div>
                        </CardHeader>
                        {rappelExpanded && (
                            <CardContent className="pt-2">
                                <div className="divide-y divide-gray-100">
                                    {rappelList.map((p, i) => (
                                        <div
                                            key={p.id || i}
                                            onClick={() => navigate(`/prospects/${p.id}`)}
                                            className="flex items-center gap-3 py-2.5 -mx-2 px-2 rounded hover:bg-purple-50/50 cursor-pointer transition-colors"
                                            data-testid={`rappel-row-${i}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                                                <BellRing className="w-3.5 h-3.5 text-purple-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-[#1B365D] truncate">{p.nom}</span>
                                                    <ImportantNotesButton
                                                        prospectId={p.id}
                                                        prospectName={p.nom}
                                                        variant="icon-only"
                                                    />
                                                    <Badge className={STATUTS.find(s => s.value === p.statut)?.color || 'bg-gray-100'}>
                                                        {STATUTS.find(s => s.value === p.statut)?.label || p.statut}
                                                    </Badge>
                                                    {p.is_late && (
                                                        <Badge className="bg-red-100 text-red-700 border border-red-200 gap-1">
                                                            <AlertCircle className="w-3 h-3" /> En retard
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                                                    {p.telephone && (
                                                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.telephone}</span>
                                                    )}
                                                    {p.next_rappel_date && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <Clock className="w-3 h-3" /> Rappel : {formatDate(p.next_rappel_date)}
                                                        </span>
                                                    )}
                                                </div>
                                                {p.last_rappel_remark && (
                                                    <p className="text-xs text-gray-600 italic mt-1 line-clamp-1">"{p.last_rappel_remark}"</p>
                                                )}
                                            </div>
                                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        )}
                    </Card>
                )}

                {/* Filters */}
                <Card className="shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input placeholder="Rechercher par nom, téléphone ou email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-prospects" />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="filter-status"><SelectValue placeholder="Filtrer par statut" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les statuts</SelectItem>
                                    {STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={sourceFilter} onValueChange={setSourceFilter}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="filter-source"><SelectValue placeholder="Filtrer par source" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes les sources</SelectItem>
                                    {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                    <SelectItem value="__none__">Non renseigné</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Prospects List */}
                <Card className="shadow-sm">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#C9A962]" />
                            Liste des prospects ({displayedProspects.length}{sourceFilter !== 'all' || statusFilter !== 'all' ? ` / ${prospects.length}` : ''})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-4 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : displayedProspects.length === 0 ? (
                            <div className="text-center py-12">
                                <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">{sourceFilter !== 'all' ? 'Aucun prospect pour cette source' : 'Aucun prospect trouvé'}</p>
                                <Button onClick={() => navigate('/prospects/nouveau')} variant="outline"
                                    disabled={!canWrite}
                                    title={!canWrite ? 'Accès refusé : permission Écriture requise' : ''}
                                    className="mt-4 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <Plus className="w-4 h-4 mr-2" />Créer un prospect
                                </Button>
                            </div>
                        ) : (
                            <>
                            {/* Mobile Card Layout */}
                            <div className="block md:hidden space-y-2 px-1">
                                {displayedProspects.map((prospect) => (
                                    <div key={prospect.id} data-testid={`prospect-card-${prospect.id}`}
                                        onClick={() => navigate(`/prospects/${prospect.id}`)}
                                        className={`rounded-lg p-3 cursor-pointer transition-all active:scale-[0.98] border border-gray-200 ${getRowStyle(prospect.statut)}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="h-9 w-9 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                    <User className="h-4 w-4 text-[#1B365D]" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-semibold text-sm text-[#1B365D] truncate">{prospect.nom}</p>
                                                        <ImportantNotesButton
                                                            prospectId={prospect.id}
                                                            prospectName={prospect.nom}
                                                            variant="icon-only"
                                                        />
                                                    </div>
                                                    {prospect.telephone && <p className="text-xs text-gray-500">{prospect.telephone}</p>}
                                                </div>
                                            </div>
                                            <div className="shrink-0 ml-2">{getStatusBadge(prospect.statut)}</div>
                                        </div>
                                        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                            <span>{formatDate(prospect.created_at)}</span>
                                            <span>{getSourceLabel(prospect.source)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Desktop Table Layout */}
                            <div className="hidden md:block overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Client</TableHead>
                                            <TableHead className="w-10 px-0"></TableHead>
                                            <TableHead>Date de création</TableHead>
                                            <TableHead>Appels</TableHead>
                                            <TableHead>Source</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {displayedProspects.map((prospect) => (
                                            <TableRow key={prospect.id} data-testid={`prospect-row-${prospect.id}`}
                                                className={`cursor-pointer hover:brightness-95 transition-all ${getRowStyle(prospect.statut)}`}
                                                onClick={() => navigate(`/prospects/${prospect.id}`)}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                            <User className="h-5 w-5 text-[#1B365D]" />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-medium text-[#1B365D]">{prospect.nom}</p>
                                                                <ImportantNotesButton
                                                                    prospectId={prospect.id}
                                                                    prospectName={prospect.nom}
                                                                    variant="icon-only"
                                                                />
                                                            </div>
                                                            {prospect.telephone && <p className="text-sm text-gray-500">{prospect.telephone}</p>}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-0" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={(e) => openHistory(prospect, e)}
                                                        className="w-7 h-7 rounded-full border-2 border-[#C9A962] flex items-center justify-center hover:bg-[#C9A962]/10 transition-colors"
                                                        title="Historique des statuts"
                                                        data-testid={`history-btn-${prospect.id}`}
                                                    >
                                                        <AlertCircle className="w-3.5 h-3.5 text-[#C9A962]" />
                                                    </button>
                                                </TableCell>
                                                <TableCell>{formatDate(prospect.created_at)}</TableCell>
                                                <TableCell><CallsBadge item={prospect} /></TableCell>
                                                <TableCell>{getSourceLabel(prospect.source)}</TableCell>
                                                <TableCell>{getStatusBadge(prospect.statut)}</TableCell>
                                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex justify-end items-center gap-4">
                                                        <Button variant="ghost" size="icon" onClick={() => navigate(`/prospects/${prospect.id}`)} title="Aperçu"
                                                            className="h-12 w-12 text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`view-prospect-${prospect.id}`}>
                                                            <Eye className="w-7 h-7" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => { setSelectedProspect(prospect); setShowDeleteModal(true); }} title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                            disabled={!canDelete}
                                                            className="h-12 w-12 text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`delete-prospect-${prospect.id}`}>
                                                            <Trash2 className="w-6 h-6" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Delete Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Supprimer le prospect</DialogTitle>
                        <DialogDescription>
                            Voulez-vous vraiment supprimer <strong>{selectedProspect?.nom}</strong> ? L'element sera deplace dans la corbeille.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
                        <Button type="button" onClick={handleDelete} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="delete-prospect-confirm">
                            {submitting ? 'Suppression...' : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Clock className="w-5 h-5 text-[#C9A962]" />
                            Historique des statuts
                        </DialogTitle>
                        {historyData && (
                            <DialogDescription>Prospect : <strong>{historyData.nom}</strong></DialogDescription>
                        )}
                    </DialogHeader>
                    <div className="py-4">
                        {loadingHistory ? (
                            <div className="flex justify-center py-8">
                                <div className="w-6 h-6 border-3 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : historyData?.history?.length > 0 ? (
                            <div className="relative">
                                {/* Timeline line */}
                                <div className="absolute left-4 top-3 bottom-3 w-0.5 bg-[#C9A962]/30" />
                                <div className="space-y-0">
                                    {historyData.history.map((entry, idx) => (
                                        <div key={idx} className="relative flex gap-4 py-3">
                                            {/* Timeline dot */}
                                            <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${idx === 0 ? 'bg-[#1B365D]' : 'bg-[#C9A962]'}`}>
                                                <span className="text-white text-xs font-bold">{idx + 1}</span>
                                            </div>
                                            {/* Content */}
                                            <div className="flex-1 bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    {entry.from_status ? (
                                                        <>
                                                            <Badge className={`${getStatusColor(entry.from_status)} text-xs`}>{getStatusLabel(entry.from_status)}</Badge>
                                                            <ArrowRight className="w-4 h-4 text-[#C9A962]" />
                                                            <Badge className={`${getStatusColor(entry.to_status)} text-xs`}>{getStatusLabel(entry.to_status)}</Badge>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-xs text-gray-500 font-medium">Créé avec le statut</span>
                                                            <Badge className={`${getStatusColor(entry.to_status)} text-xs`}>{getStatusLabel(entry.to_status)}</Badge>
                                                        </>
                                                    )}
                                                </div>
                                                {entry.remarque && (
                                                    <p className="text-sm text-[#1B365D] italic bg-amber-50/60 border border-amber-100 rounded px-2.5 py-1.5 mb-2 whitespace-pre-wrap break-words" data-testid={`history-remarque-${idx}`}>
                                                        « {entry.remarque} »
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {formatDateTime(entry.changed_at)}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <User className="w-3 h-3" />
                                                        {entry.changed_by_name}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                <p>Aucun historique disponible</p>
                                <p className="text-xs mt-1">L'historique sera enregistré à partir des prochains changements de statut</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowHistoryModal(false)}>Fermer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
